import { getCountdownTarget } from './stickyBanner.helper';
import { DEVICES, getDeviceKey } from './devices';
import { DeviceKey, DisplayRules, RulesData, SiteTimezone } from './types';

/**
 * The "Devices" range of a viewport width (same widths as the template's
 * Screen settings: desktop, 1366, 1280, 1024, 768, 640).
 *
 * @param width Viewport width in px.
 */
export const getDeviceType = ( width: number ): DeviceKey =>
	getDeviceKey( width );

/**
 * Convert "YYYY-MM-DDTHH:MM" (site timezone) to epoch ms.
 *
 * @param value        Date-time string.
 * @param siteTimezone Site timezone.
 */
export const siteDateTimeToEpoch = (
	value: string,
	siteTimezone: SiteTimezone
): number | null => {
	if ( ! value ) {
		return null;
	}
	return getCountdownTarget(
		{
			endDate: value.slice( 0, 10 ),
			endTime: value.slice( 11, 16 ),
			timezone: 'site',
			afterExpiry: 'hide',
		},
		siteTimezone
	);
};

/**
 * Day of week (0 = Sunday) in the site timezone.
 *
 * @param now          Epoch ms.
 * @param siteTimezone Site timezone.
 */
export const getSiteWeekday = (
	now: number,
	siteTimezone: SiteTimezone
): number => {
	if ( siteTimezone.timezone ) {
		try {
			const name = new Intl.DateTimeFormat( 'en-US', {
				timeZone: siteTimezone.timezone,
				weekday: 'short',
			} ).format( now );
			const index = [
				'Sun',
				'Mon',
				'Tue',
				'Wed',
				'Thu',
				'Fri',
				'Sat',
			].indexOf( name );
			if ( index >= 0 ) {
				return index;
			}
		} catch ( e ) {
			// Fall back to the offset below.
		}
	}
	return new Date( now + siteTimezone.gmtOffset * 3600000 ).getUTCDay();
};

export type ScheduleState = 'off' | 'upcoming' | 'running' | 'ended';

/**
 * @param rules        Display rules.
 * @param siteTimezone Site timezone.
 * @param now          Epoch ms.
 */
export const getScheduleState = (
	rules: DisplayRules,
	siteTimezone: SiteTimezone,
	now: number
): ScheduleState => {
	if ( ! rules.schedule.enabled ) {
		return 'off';
	}
	const start = siteDateTimeToEpoch( rules.schedule.start, siteTimezone );
	const end = siteDateTimeToEpoch( rules.schedule.end, siteTimezone );
	if ( start !== null && now < start ) {
		return 'upcoming';
	}
	if ( end !== null && now >= end ) {
		return 'ended';
	}
	return 'running';
};

/**
 * Whether the schedule allows the banner right now (date range + weekday).
 *
 * @param rules        Display rules.
 * @param siteTimezone Site timezone.
 * @param now          Epoch ms.
 */
export const isWithinSchedule = (
	rules: DisplayRules,
	siteTimezone: SiteTimezone,
	now: number
): boolean => {
	const state = getScheduleState( rules, siteTimezone, now );
	if ( state === 'off' ) {
		return true;
	}
	if ( state !== 'running' ) {
		return false;
	}
	return rules.schedule.days.includes( getSiteWeekday( now, siteTimezone ) );
};

/* ---------------------------------------------------------------------------
 * Visitor storage (all wrapped: storage can be blocked)
 * ------------------------------------------------------------------------- */
const CLOSED_KEY_PREFIX = 'wplms_sb_closed_';

const safeGet = (
	storage: 'local' | 'session',
	key: string
): string | null => {
	try {
		return (
			storage === 'local' ? window.localStorage : window.sessionStorage
		).getItem( key );
	} catch ( e ) {
		return null;
	}
};

const safeSet = (
	storage: 'local' | 'session',
	key: string,
	value: string
) => {
	try {
		( storage === 'local'
			? window.localStorage
			: window.sessionStorage
		).setItem( key, value );
	} catch ( e ) {
		// Ignore (private mode / blocked storage).
	}
};

/**
 * A visitor is "returning" when they had a previous browser session.
 * Stable for the whole current session.
 */
export const isReturningVisitor = (): boolean => {
	const sessionValue = safeGet( 'session', 'wplms_sb_returning' );
	if ( sessionValue !== null ) {
		return sessionValue === '1';
	}
	const returning = safeGet( 'local', 'wplms_sb_seen' ) !== null;
	safeSet( 'local', 'wplms_sb_seen', String( Date.now() ) );
	safeSet( 'session', 'wplms_sb_returning', returning ? '1' : '0' );
	return returning;
};

/**
 * @param bannerId Banner id.
 */
export const isClosedByVisitor = ( bannerId: string ): boolean => {
	const value = Number( safeGet( 'local', CLOSED_KEY_PREFIX + bannerId ) );
	return Number.isFinite( value ) && value > Date.now();
};

/**
 * @param bannerId Banner id.
 * @param hours    How long to keep it hidden. 0 = do not remember.
 */
export const rememberClose = ( bannerId: string, hours: number ): void => {
	if ( hours <= 0 ) {
		return;
	}
	safeSet(
		'local',
		CLOSED_KEY_PREFIX + bannerId,
		String( Date.now() + hours * 3600000 )
	);
};

/* ---------------------------------------------------------------------------
 * Human readable summary (admin)
 * ------------------------------------------------------------------------- */
/**
 * Short sentences describing where/when a banner shows.
 *
 * @param rules     Display rules.
 * @param rulesData Dynamic site data (optional).
 */
export const summarizeRules = (
	rules: DisplayRules,
	rulesData?: RulesData | null
): { where: string; who: string; devices: string; placement: string } => {
	const hiddenPages = rules.showOnAllPages ? 0 : rules.hiddenPageIds.length;
	const totalPages = rulesData?.pages.length;

	let where = 'All pages';
	if (
		hiddenPages &&
		totalPages !== undefined &&
		hiddenPages >= totalPages
	) {
		where = 'Hidden on all pages';
	} else if ( hiddenPages ) {
		where = `All pages except ${ hiddenPages }`;
	}

	const loginLabel = {
		all: 'Everyone',
		logged_in: 'Logged-in users',
		logged_out: 'Logged-out visitors',
	}[ rules.visitors.loginState ];
	const visitorLabel = {
		all: '',
		new: ' · new visitors',
		returning: ' · returning visitors',
	}[ rules.visitors.visitorType ];
	const rolesLabel =
		rules.visitors.loginState !== 'logged_out' &&
		rules.visitors.roles.length
			? ` (${ rules.visitors.roles.length } role${
					rules.visitors.roles.length > 1 ? 's' : ''
			  })`
			: '';

	const deviceNames = DEVICES.filter( ( item ) => rules.devices[ item.key ] );
	const devices =
		deviceNames.length === DEVICES.length
			? 'All devices'
			: deviceNames
					.map( ( item ) =>
						item.key === 'desktop'
							? item.label
							: `${ item.label }px`
					)
					.join( ', ' ) || 'No device';

	const { position, behavior } = rules.placement;
	const triggerLabel =
		rules.trigger.type === 'delay'
			? ` · after ${ rules.trigger.delaySeconds }s`
			: rules.trigger.type === 'scroll'
			? ` · after ${ rules.trigger.scrollPercent }% scroll`
			: '';

	return {
		where,
		who: loginLabel + rolesLabel + visitorLabel,
		devices,
		placement: `${ position === 'top' ? 'Top' : 'Bottom' } · ${
			behavior === 'fixed' ? 'Sticky' : 'Static'
		}${ triggerLabel }`,
	};
};
